import {
  trainIdentifierIsEqual,
  TrainIdentifierWithIncidentId,
  TrainIdentifierWithIncidentIdString,
  trainIdentifierWithIncidentIdToString,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import {
  AffectedTrain,
  AffectedTrainsResponse,
} from "features/CenterContent/RoleContent/Vaktleder/types";
import { Dispatch, SetStateAction } from "react";

// TRAINS TO SHOW
const SHOW_AFFECTED_TRAINS_GRACE_PERIOD = 900_000; // 900s, 15m

export function calculateTrainsToShow(
  affectedTrainsData: AffectedTrainsResponse | undefined,
  selectedTrains: AffectedTrain[],
  showPassedTrains: boolean,
) {
  if (!affectedTrainsData) {
    return [];
  }

  if (showPassedTrains) {
    return affectedTrainsData.affectedTrains.filter(
      ({ state }) => state !== "NOT_AFFECTED",
    );
  }

  return affectedTrainsData.affectedTrains.filter((affectedTrain) => {
    if (affectedTrain.state === "NOT_AFFECTED") return false;

    const time =
      affectedTrain.incidentExitTime ?? affectedTrain.incidentArrivalTime;
    if (!time) return true;

    return (
      // Grace period is the amount of time(15min) necessary for the train to deemed as passed (for "Vis passerte tog")
      time.getTime() + SHOW_AFFECTED_TRAINS_GRACE_PERIOD >=
        new Date().getTime() ||
      selectedTrains.find(
        ({ trainId, incidentId }) =>
          trainIdentifierIsEqual(trainId, affectedTrain.trainId) &&
          incidentId === affectedTrain.incidentId,
      )
    );
  });
}

// SELECTED TRAINS
export function updateSelectedTrainsState(
  affectedTrain: AffectedTrain,
  setSelectedTrains: Dispatch<SetStateAction<AffectedTrain[]>>,
) {
  setSelectedTrains((prevSelectedTrains) => {
    const idx = prevSelectedTrains.findIndex(
      (selectedTrain) =>
        trainIdentifierIsEqual(selectedTrain.trainId, affectedTrain.trainId) &&
        selectedTrain.incidentId === affectedTrain.incidentId,
    );

    if (idx !== -1) {
      return prevSelectedTrains.toSpliced(idx, 1);
    }

    return [...prevSelectedTrains, affectedTrain];
  });
}

export function batchUpdateSelectedTrainsState(
  affectedTrains: AffectedTrain[],
  setSelectedTrains: Dispatch<SetStateAction<AffectedTrain[]>>,
) {
  setSelectedTrains((prevSelectedTrains) => {
    if (affectedTrains.length === 0) {
      return prevSelectedTrains;
    }

    const hasSameIncidentSelection =
      prevSelectedTrains.length > 0 &&
      affectedTrains[0].incidentId === prevSelectedTrains[0].incidentId;

    if (!hasSameIncidentSelection) {
      return affectedTrains;
    }

    const allAffectedTrainsAreSelected = affectedTrains.every((affectedTrain) =>
      prevSelectedTrains.some(
        (selectedTrain) =>
          trainIdentifierIsEqual(
            selectedTrain.trainId,
            affectedTrain.trainId,
          ) && selectedTrain.incidentId === affectedTrain.incidentId,
      ),
    );

    if (allAffectedTrainsAreSelected) {
      return prevSelectedTrains.filter(
        (selectedTrain) =>
          !affectedTrains.some(
            (affectedTrain) =>
              trainIdentifierIsEqual(
                selectedTrain.trainId,
                affectedTrain.trainId,
              ) && selectedTrain.incidentId === affectedTrain.incidentId,
          ),
      );
    }

    const missingAffectedTrains = affectedTrains.filter(
      (affectedTrain) =>
        !prevSelectedTrains.some(
          (selectedTrain) =>
            trainIdentifierIsEqual(
              selectedTrain.trainId,
              affectedTrain.trainId,
            ) && selectedTrain.incidentId === affectedTrain.incidentId,
        ),
    );

    return [...prevSelectedTrains, ...missingAffectedTrains];
  });
}

// PENDING TRAINS
export function addPendingTrainsToState(
  trains: TrainIdentifierWithIncidentId[],
  setPendingTrains: Dispatch<
    SetStateAction<TrainIdentifierWithIncidentIdString[]>
  >,
) {
  setPendingTrains((prevPendingTrains) => {
    const newTrainIds = trains
      .map(trainIdentifierWithIncidentIdToString)
      .filter((trainId) => !prevPendingTrains.includes(trainId));

    return [...prevPendingTrains, ...newTrainIds];
  });
}

export function removePendingTrainsFromState(
  trains: TrainIdentifierWithIncidentId[],
  setPendingTrains: Dispatch<
    SetStateAction<TrainIdentifierWithIncidentIdString[]>
  >,
) {
  setPendingTrains((prevPendingTrains) => {
    const idsToRemove = trains.map(trainIdentifierWithIncidentIdToString);

    return prevPendingTrains.filter(
      (trainId) => !idsToRemove.includes(trainId),
    );
  });
}
